import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@material-ui/core';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { formationApi } from '../../../api/formationApi';
import { questionnaireApi } from '../../../api/questionnaireApi';
import { getTone, grey, toneAlpha } from '../../../theme/tones';

/**
 * Import des réponses d'une session, depuis un CSV.
 *
 * Une ligne = une réponse : l'apprenant, le questionnaire, l'intitulé de la
 * question et ce qui a été répondu. Un seul fichier porte donc les trois
 * questionnaires et tous les répondants.
 *
 * Le modèle n'est pas générique : il est **construit à partir de la session
 * choisie**, une ligne par inscrit et par question, libellés exacts inclus.
 * C'est ce qui évite le seul vrai piège du format — un intitulé recopié de
 * travers fait rejeter la ligne, et personne ne devine pourquoi.
 *
 * Le format et les contrôles vivent côté serveur
 * (`QuestionnaireModel::importAnswers`). COLONNES doit rester aligné sur
 * IMPORT_ANSWER_COLUMNS.
 */

const COLONNES = ['email', 'type_questionnaire', 'question', 'reponse'];

/** Séparateur des réponses multiples, comme pour l'import des questionnaires. */
const SEP = '|';

/** Échappe une cellule pour le CSV : guillemets doublés si nécessaire. */
const cellule = (v) => {
  const s = String(v == null ? '' : v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const QuestionnaireAnswersImport = ({ onImported }) => {
  const [ouvert, setOuvert] = useState(false);
  const [formations, setFormations] = useState([]);
  const [formation, setFormation] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [modele, setModele] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [fichier, setFichier] = useState(null);
  const [csv, setCsv] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreurs, setErreurs] = useState([]);
  const [avertissements, setAvertissements] = useState([]);
  const inputRef = useRef(null);

  const accent = getTone('teal');

  useEffect(() => {
    if (!ouvert || formations.length > 0) return;
    (async () => {
      try {
        const data = await formationApi.getFormationOptions(window.localStorage.getItem('accessToken'));
        setFormations(data || []);
      } catch (err) {
        console.error(err);
        toast.error('Impossible de charger les formations.');
      }
    })();
  }, [ouvert, formations.length]);

  useEffect(() => {
    setSession(null);
    setModele(null);
    if (!formation) {
      setSessions([]);
      return;
    }
    (async () => {
      try {
        const data = await questionnaireApi.getSessionsByFormationId(
          formation.id,
          window.localStorage.getItem('accessToken')
        );
        setSessions(data || []);
      } catch (err) {
        console.error(err);
        setSessions([]);
      }
    })();
  }, [formation]);

  // Inscrits et questions de la session : ce sont eux qui composent le modèle.
  useEffect(() => {
    setModele(null);
    if (!session) return;
    (async () => {
      setChargement(true);
      try {
        const data = await questionnaireApi.getAnswersTemplate(
          session.id,
          window.localStorage.getItem('accessToken')
        );
        setModele(data);
      } catch (err) {
        console.error(err);
        toast.error('Impossible de préparer le modèle.');
      } finally {
        setChargement(false);
      }
    })();
  }, [session]);

  /**
   * Le modèle, une ligne par inscrit et par question.
   *
   * Les options autorisées sont rappelées dans une colonne de commentaire :
   * elle n'est pas lue à l'import, elle évite d'aller les rechercher ailleurs.
   */
  const telechargerModele = () => {
    if (!modele) return;
    const lignes = [[...COLONNES, 'options_possibles (aide, non importée)']];

    modele.learners.forEach((l) => {
      modele.questions.forEach((q) => {
        lignes.push([
          l.email,
          q.questionnaire_type,
          q.text,
          '',
          q.type === 'open' ? 'Réponse libre' : (q.options || []).join(SEP)
        ]);
      });
    });

    const contenu = lignes.map((l) => l.map(cellule).join(';')).join('\r\n');
    const blob = new Blob([`﻿${contenu}\r\n`], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reponses-session-${session ? session.number : ''}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const fermer = () => {
    setOuvert(false);
    setFormation(null);
    setSessions([]);
    setSession(null);
    setModele(null);
    setFichier(null);
    setCsv('');
    setErreurs([]);
    setAvertissements([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const choisirFichier = (e) => {
    const f = e.target.files && e.target.files[0];
    setErreurs([]);
    setAvertissements([]);
    if (!f) {
      setFichier(null);
      setCsv('');
      return;
    }
    setFichier(f);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ''));
    reader.onerror = () => {
      toast.error('Le fichier n’a pas pu être lu.');
      setFichier(null);
      setCsv('');
    };
    reader.readAsText(f, 'UTF-8');
  };

  const importer = async () => {
    if (!session || !csv) return;
    setEnvoi(true);
    setErreurs([]);
    setAvertissements([]);
    try {
      const res = await questionnaireApi.importAnswers(
        { csv, session_id: session.id },
        window.localStorage.getItem('accessToken')
      );
      setAvertissements((res && res.warnings) || []);
      if (!res || res.status !== 200) {
        setErreurs((res && res.errors) || []);
        toast.error((res && res.message) || "L'import a échoué.");
        return;
      }
      toast.success(res.message || 'Réponses importées.');
      if (onImported) onImported();
      if (!res.warnings || res.warnings.length === 0) fermer();
    } catch (err) {
      console.error(err);
      toast.error("L'import a échoué.");
    } finally {
      setEnvoi(false);
    }
  };

  const libelleSession = (s) => {
    if (!s) return '';
    const date = s.start_date ? new Date(s.start_date).toLocaleDateString('fr-FR') : 'date à définir';
    return `N° ${s.number} • ${date}${s.status ? ` • ${s.status}` : ''}`;
  };

  const prete = modele && modele.learners.length > 0 && modele.questions.length > 0;

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<UploadFileIcon fontSize="small" />}
        onClick={() => setOuvert(true)}
        sx={{
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 800,
          borderColor: accent.border,
          color: accent.color,
          bgcolor: accent.bg
        }}
      >
        Importer des réponses
      </Button>

      <Dialog open={ouvert} onClose={fermer} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Importer des réponses</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
            Le modèle dépend de la session : il porte ses inscrits et les intitulés exacts
            de ses questions. Choisissez-la d’abord, le téléchargement apparaît ensuite.
            Une réponse déjà enregistrée n’est jamais remplacée.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Autocomplete
              options={formations}
              value={formation}
              onChange={(e, v) => setFormation(v)}
              getOptionLabel={(o) => (o ? `${o.name}${o.programNumberAll ? ` (N° ${o.programNumberAll})` : ''}` : '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="1 · Formation" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }} />
              )}
            />
            <Autocomplete
              options={sessions}
              value={session}
              disabled={!formation}
              onChange={(e, v) => setSession(v)}
              getOptionLabel={libelleSession}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              noOptionsText="Aucune session pour cette formation"
              renderInput={(params) => (
                <TextField {...params} label="2 · Session" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }} />
              )}
            />

            {session && (
              <Box sx={{ p: 2, borderRadius: '14px', bgcolor: grey(50), border: `1px solid ${grey(200)}` }}>
                {chargement && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress size={16} />
                    <Typography sx={{ fontSize: '0.84rem' }}>Préparation du modèle…</Typography>
                  </Box>
                )}
                {!chargement && modele && (
                  <>
                    <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em', fontWeight: 900, color: grey(500), mb: 0.5 }}>
                      3 · LE MODÈLE À REMPLIR
                    </Typography>
                    <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: 'text.primary' }}>
                      {modele.learners.length} inscrit(s) × {modele.questions.length} question(s)
                      {prete ? ` = ${modele.learners.length * modele.questions.length} lignes à remplir` : ''}
                    </Typography>
                    {!prete && (
                      <Typography sx={{ fontSize: '0.8rem', color: getTone('amber').color, mt: 0.5 }}>
                        {modele.questions.length === 0
                          ? 'Cette session n’a pas encore de questionnaire.'
                          : 'Aucun inscrit avec une adresse e-mail sur cette session.'}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon fontSize="small" />}
                      onClick={telechargerModele}
                      disabled={!prete}
                      sx={{
                        mt: 1.25,
                        textTransform: 'none',
                        fontWeight: 800,
                        borderRadius: '10px',
                        bgcolor: accent.color,
                        '&:hover': { bgcolor: accent.deep }
                      }}
                    >
                      Télécharger le modèle
                    </Button>
                  </>
                )}
              </Box>
            )}

            <Box
              onClick={() => inputRef.current && inputRef.current.click()}
              sx={{
                p: 2.5,
                borderRadius: '16px',
                border: `1px dashed ${fichier ? accent.border : grey(300)}`,
                bgcolor: fichier ? toneAlpha('teal', 0.06) : 'transparent',
                textAlign: 'center',
                cursor: 'pointer'
              }}
            >
              <UploadFileIcon sx={{ fontSize: 26, color: fichier ? accent.color : grey(400) }} />
              <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em', fontWeight: 900, color: grey(500) }}>
                4 · LE FICHIER REMPLI
              </Typography>
              <Typography sx={{ mt: 0.5, fontWeight: 800, fontSize: '0.9rem' }}>
                {fichier ? fichier.name : 'Choisir le fichier CSV rempli'}
              </Typography>
              <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={choisirFichier} />
            </Box>

            {erreurs.length > 0 && (
              <Alert severity="error" sx={{ borderRadius: '14px' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.5 }}>
                  Rien n’a été importé. À corriger dans le fichier :
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 200, overflow: 'auto' }}>
                  {erreurs.map((e) => (
                    <li key={e}><Typography sx={{ fontSize: '0.82rem' }}>{e}</Typography></li>
                  ))}
                </Box>
              </Alert>
            )}

            {avertissements.length > 0 && (
              <Alert severity="info" sx={{ borderRadius: '14px' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.5 }}>
                  Réponses déjà présentes, laissées intactes :
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 180, overflow: 'auto' }}>
                  {avertissements.map((w) => (
                    <li key={w}><Typography sx={{ fontSize: '0.82rem' }}>{w}</Typography></li>
                  ))}
                </Box>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={fermer} color="inherit" sx={{ fontWeight: 700 }}>Fermer</Button>
          <Button
            onClick={importer}
            variant="contained"
            disabled={!session || !csv || envoi}
            sx={{ borderRadius: '12px', px: 3, fontWeight: 800 }}
          >
            {envoi ? 'Import en cours…' : 'Importer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

QuestionnaireAnswersImport.propTypes = {
  onImported: PropTypes.func
};

export default QuestionnaireAnswersImport;
